import { useState } from 'react'

import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Chip from '@mui/material/Chip'
import CircularProgress from '@mui/material/CircularProgress'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import Divider from '@mui/material/Divider'
import Drawer from '@mui/material/Drawer'
import IconButton from '@mui/material/IconButton'
import Stack from '@mui/material/Stack'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'

import { ApiError } from '../api/client'
import type { Severity } from '../api/types'
import {
  useAcknowledge,
  useEscalate,
  useExceptionDetail,
  useResolve,
} from '../hooks/useApi'
import { tokens } from '../theme'

export function ExceptionDrawer({
  id,
  onClose,
}: {
  id: string | null
  onClose: () => void
}) {
  const open = !!id
  const { data, isLoading, isError, error } = useExceptionDetail(id)

  const ack = useAcknowledge()
  const resolve = useResolve()
  const escalate = useEscalate()

  const [resolveOpen, setResolveOpen] = useState(false)
  const [escalateOpen, setEscalateOpen] = useState(false)
  const [resolveReason, setResolveReason] = useState('')
  const [escTier, setEscTier] = useState('on-call-dispatcher')
  const [escNote, setEscNote] = useState('')

  async function handleAck() {
    if (!id) return
    await ack.mutateAsync(id)
  }
  async function handleResolveSubmit() {
    if (!id) return
    await resolve.mutateAsync({ id, close_reason: resolveReason.trim() })
    setResolveOpen(false)
    setResolveReason('')
    onClose()
  }
  async function handleEscalateSubmit() {
    if (!id) return
    await escalate.mutateAsync({ id, tier: escTier, note: escNote.trim() })
    setEscalateOpen(false)
    setEscNote('')
  }

  const busy = ack.isPending || resolve.isPending || escalate.isPending
  const mutationError =
    (ack.error || resolve.error || escalate.error) as ApiError | null | undefined

  return (
    <>
      <Drawer
        anchor="right"
        open={open}
        onClose={onClose}
        slotProps={{ paper: { sx: { width: { xs: '100%', md: 460 } } } }}
      >
        <Box sx={{ p: 2.5, display: 'flex', flexDirection: 'column', height: '100%' }}>
          <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
            <Typography variant="h4" sx={{ fontWeight: 700, flexGrow: 1 }}>
              Exception
            </Typography>
            <IconButton size="small" onClick={onClose} aria-label="close">
              ✕
            </IconButton>
          </Stack>

          {isLoading && (
            <Box sx={{ py: 6, display: 'flex', justifyContent: 'center' }}>
              <CircularProgress size={24} />
            </Box>
          )}

          {isError && (
            <Alert severity="error" variant="outlined" sx={{ mt: 2 }}>
              {error instanceof Error ? error.message : 'Failed to load exception'}
            </Alert>
          )}

          {data && (
            <Stack spacing={2} sx={{ mt: 2, flexGrow: 1, overflowY: 'auto' }}>
              <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                <SeverityPill severity={data.severity} />
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {data.code}
                </Typography>
                {data.category && (
                  <Chip label={data.category} size="small" variant="outlined" />
                )}
                <Box sx={{ flexGrow: 1 }} />
                {data.acknowledged_at && (
                  <Chip
                    label="acknowledged"
                    size="small"
                    sx={{ bgcolor: tokens.goldLight, color: tokens.charcoal }}
                  />
                )}
              </Stack>

              <Typography variant="h5" sx={{ fontWeight: 600 }}>
                {data.subject_summary || data.subject_type}
              </Typography>

              {data.suggested_action && (
                <Alert severity="info" variant="outlined">
                  {data.suggested_action}
                </Alert>
              )}

              <Section title="Evidence">
                <EvidenceTable evidence={data.evidence} />
              </Section>

              <Section title={`Timeline${data.timeline ? ` (${data.timeline.length})` : ''}`}>
                {data.timeline && data.timeline.length > 0 ? (
                  <Stack spacing={1}>
                    {data.timeline.slice(0, 20).map((ev) => (
                      <Stack
                        key={ev.id}
                        direction="row"
                        spacing={1}
                        sx={{ fontSize: 12.5, color: 'text.secondary' }}
                      >
                        <Typography variant="caption" sx={{ minWidth: 120 }}>
                          {new Date(ev.event_at).toLocaleString()}
                        </Typography>
                        <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: 12 }}>
                          {ev.event_kind}
                        </Typography>
                      </Stack>
                    ))}
                  </Stack>
                ) : (
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    No events recorded for this subject.
                  </Typography>
                )}
              </Section>

              {mutationError && (
                <Alert severity="error" variant="outlined">
                  {mutationError.message}
                </Alert>
              )}
            </Stack>
          )}

          <Divider sx={{ my: 2 }} />
          <Stack direction="row" spacing={1}>
            <Button
              variant="outlined"
              onClick={handleAck}
              disabled={busy || !data || !!data.acknowledged_at}
            >
              {data?.acknowledged_at ? 'Acknowledged' : 'Acknowledge'}
            </Button>
            <Button
              variant="outlined"
              color="warning"
              onClick={() => setEscalateOpen(true)}
              disabled={busy || !data}
            >
              Escalate
            </Button>
            <Box sx={{ flexGrow: 1 }} />
            <Button
              variant="contained"
              color="secondary"
              onClick={() => setResolveOpen(true)}
              disabled={busy || !data}
            >
              Resolve
            </Button>
          </Stack>
        </Box>
      </Drawer>

      {/* Resolve dialog */}
      <Dialog open={resolveOpen} onClose={() => setResolveOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle>Resolve exception</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            multiline
            minRows={3}
            label="Close reason"
            placeholder="e.g. Engineer on-site, leak repaired"
            value={resolveReason}
            onChange={(e) => setResolveReason(e.target.value)}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setResolveOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            color="secondary"
            disabled={resolveReason.trim().length < 3 || resolve.isPending}
            onClick={handleResolveSubmit}
          >
            Resolve
          </Button>
        </DialogActions>
      </Dialog>

      {/* Escalate dialog */}
      <Dialog open={escalateOpen} onClose={() => setEscalateOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle>Escalate exception</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Tier"
              value={escTier}
              onChange={(e) => setEscTier(e.target.value)}
              helperText="e.g. on-call-dispatcher, ops-manager"
            />
            <TextField
              label="Note"
              multiline
              minRows={3}
              value={escNote}
              onChange={(e) => setEscNote(e.target.value)}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEscalateOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            color="warning"
            disabled={escNote.trim().length < 3 || !escTier || escalate.isPending}
            onClick={handleEscalateSubmit}
          >
            Escalate
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <Box>
      <Typography variant="overline" sx={{ color: 'text.secondary' }}>
        {title}
      </Typography>
      <Box sx={{ mt: 0.5 }}>{children}</Box>
    </Box>
  )
}

function EvidenceTable({ evidence }: { evidence?: Record<string, unknown> }) {
  if (!evidence || Object.keys(evidence).length === 0) {
    return (
      <Typography variant="body2" sx={{ color: 'text.secondary' }}>
        —
      </Typography>
    )
  }
  return (
    <Stack spacing={0.5}>
      {Object.entries(evidence).map(([k, v]) => (
        <Stack key={k} direction="row" spacing={1} sx={{ fontSize: 12.5 }}>
          <Typography variant="caption" sx={{ minWidth: 140, color: 'text.secondary' }}>
            {k}
          </Typography>
          <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: 12 }}>
            {typeof v === 'string' || typeof v === 'number' || typeof v === 'boolean'
              ? String(v)
              : JSON.stringify(v)}
          </Typography>
        </Stack>
      ))}
    </Stack>
  )
}

function SeverityPill({ severity }: { severity: Severity }) {
  const colour =
    severity === 'critical' ? tokens.alert :
    severity === 'high' ? '#E09935' :
    severity === 'warn' ? tokens.gold :
    tokens.textSec
  return (
    <Chip
      label={severity}
      size="small"
      sx={{
        bgcolor: colour,
        color: 'common.white',
        fontWeight: 700,
        textTransform: 'uppercase',
        fontSize: 10,
      }}
    />
  )
}
